import { Container, Graphics, type FederatedPointerEvent } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { sectorAt } from '@/config/sectors';
import type { RunStats } from '@/core/records';
import { getActiveDevice } from '@/input/active-device';
import { t, type I18nKey } from '@/i18n';
import { sfx } from '@/audio/sfx';
import { buildSectorLostBody } from './gameover-sector';
import { buildRunSummary } from './gameover-cards';
import { buildVictoryBody } from './victory';
import { bodyText, chamferPanel, label, targetBrackets } from './widgets';

export interface RunEndCallbacks {
  /** NEXT SECTOR / PLAY AGAIN (victory) or RETRY SECTOR (defeat) */
  primary(): void;
  menu(): void;
}

/** Everything the sector run-end screens show (victory + defeat). */
export interface RunEndData {
  kind: 'victory' | 'defeat';
  sector: number;
  stats: RunStats;
  improved: ReadonlyArray<keyof RunStats>;
  seed: number;
  totalRuns: number;
  /** Achievements earned during this run */
  earned: ReadonlyArray<string>;
  /** Victory: fight time of the clear + best-time info */
  clearTime: number;
  newBest: boolean;
  best: number | null;
  /** What the win opened (null: nothing new) */
  unlock: { kind: 'sector'; index: number } | { kind: 'endless' } | null;
  xp: number;
  /** Victory: an open next sector exists (NEXT SECTOR, else PLAY AGAIN) */
  hasNext: boolean;
  /** Defeat: the room the ship fell in (0-based) and the sector's room count */
  room: number;
  rooms: number;
}

/** Fixed design space — scaled to fill the real canvas (gameover.ts pattern). */
const DESIGN_W = 1280;
const DESIGN_H = 720;
const BTN = { y: 642, h: 52, gap: 30, primaryW: 300, menuW: 240 };

/**
 * Sector run end (EPIC D3): VICTORY (gold) or SHIP DESTROYED (red). The
 * left column reuses the game-over run summary; the body is ui/victory.ts
 * or ui/gameover-sector.ts. Two buttons: the primary action + MAIN MENU.
 */
export class RunEndScreen {
  readonly view = new Container();
  private readonly dim = new Graphics();
  private readonly layer = new Container();
  open = false;
  private selected = 0;
  private data: RunEndData | null = null;

  constructor(private readonly callbacks: RunEndCallbacks) {
    this.view.visible = false;
    this.view.addChild(this.dim, this.layer);
  }

  show(screenW: number, screenH: number, data: RunEndData): void {
    this.open = true;
    this.view.visible = true;
    this.data = data;
    this.selected = 0; // the primary action — one press goes on
    this.resize(screenW, screenH);
    this.rebuild();
  }

  /** Re-fit dim + design-space scale (fullscreen toggles, window resizes). */
  resize(screenW: number, screenH: number): void {
    const tint = this.data?.kind === 'victory' ? 0x0a0e16 : 0x1a0508;
    this.dim.clear().rect(0, 0, screenW, screenH).fill({ color: tint, alpha: 0.93 });
    const scale = Math.min(screenW / DESIGN_W, screenH / DESIGN_H);
    this.layer.scale.set(scale);
    this.layer.position.set((screenW - DESIGN_W * scale) / 2, (screenH - DESIGN_H * scale) / 2);
  }

  moveSelection(dir: number): void {
    if (dir === 0) return;
    this.selected = 1 - this.selected; // two buttons: any step flips
    sfx.uiMove();
    this.rebuild();
  }

  activateSelected(): void {
    if (!this.open) return;
    sfx.uiConfirm();
    if (this.selected === 0) this.callbacks.primary();
    else this.callbacks.menu();
  }

  hide(): void {
    this.open = false;
    this.view.visible = false;
  }

  private rebuild(): void {
    const data = this.data;
    if (!data) return;
    this.layer.removeChildren().forEach((c) => c.destroy({ children: true }));
    const win = data.kind === 'victory';
    const title = label(
      t(win ? 'victory.title' : 'gameover.title'), 40, win ? COLORS.gemYellow : COLORS.dangerRed, 6,
    );
    title.anchor.set(0.5, 0);
    title.position.set(DESIGN_W / 2, 18);
    const sector = sectorAt(data.sector);
    const sub = label(
      `${t('sector.label', { n: data.sector + 1 })} · ${t(sector.nameKey as I18nKey)}`,
      16, sector.accent, 3,
    );
    sub.anchor.set(0.5, 0);
    sub.position.set(DESIGN_W / 2, 66);
    this.layer.addChild(title, sub);
    this.layer.addChild(buildRunSummary(data.stats, data.improved, data.seed, data.totalRuns));
    this.layer.addChild(win ? buildVictoryBody(data) : buildSectorLostBody(data));
    this.buildButtons(data);
    this.buildHint();
  }

  private buildButtons(data: RunEndData): void {
    const primaryKey: I18nKey = data.kind === 'defeat'
      ? 'sectorLost.retry'
      : data.hasNext ? 'victory.next' : 'victory.replay';
    const defs = [
      { key: primaryKey, w: BTN.primaryW },
      { key: 'victory.menu' as I18nKey, w: BTN.menuW },
    ];
    const total = BTN.primaryW + BTN.menuW + BTN.gap;
    let x = (DESIGN_W - total) / 2;
    defs.forEach((d, i) => {
      const selected = this.selected === i;
      const btn = new Container();
      const bg = new Graphics();
      chamferPanel(bg, d.w, BTN.h, {
        fill: selected ? 0x2a2440 : 0x161222,
        stroke: selected ? COLORS.uiAccent : 0x4a4256,
        strokeWidth: selected ? 4 : 3,
        notch: 12,
      });
      if (selected) targetBrackets(bg, d.w, BTN.h);
      const txt = label(t(d.key), 19, selected ? COLORS.playerYellow : 0xffffff, 2);
      txt.anchor.set(0.5);
      txt.position.set(d.w / 2, BTN.h / 2);
      btn.addChild(bg, txt);
      btn.position.set(x, BTN.y);
      btn.eventMode = 'static';
      btn.cursor = 'pointer';
      btn.on('pointerover', (e: FederatedPointerEvent) => {
        if (e.pointerType === 'mouse' && this.selected !== i) {
          this.selected = i;
          this.rebuild();
        }
      });
      btn.on('pointertap', () => {
        this.selected = i;
        this.activateSelected();
      });
      this.layer.addChild(btn);
      x += d.w + BTN.gap;
    });
  }

  private buildHint(): void {
    const device = getActiveDevice();
    const key: I18nKey = device === 'gamepad' ? 'runEnd.hint.gamepad'
      : device === 'touch' ? 'runEnd.hint.touch' : 'runEnd.hint.keyboard';
    const hint = bodyText(t(key), 14, 0x6a7076);
    hint.anchor.set(0.5, 1);
    hint.position.set(DESIGN_W / 2, DESIGN_H - 10);
    this.layer.addChild(hint);
  }
}
